import { Check, X, MapPin, ListChecks, Pencil, Zap, PenLine, ClipboardList, CheckCircle, PlusCircle } from 'lucide-react';
import { z } from 'zod';
import type { IPendingAction } from '../../store/useChatStore';

// --- Zod schemas for parsing action args ---

const ZCreateAreaArgs = z.object({
  name: z.string(),
});

const ZTaskItem = z.object({
  name: z.string(),
  points: z.number(),
  frequency_number: z.number(),
  frequency_unit: z.string(),
  is_high_priority: z.boolean().optional(),
});

const ZCreateTaskArgs = ZTaskItem.extend({
  area_uuid: z.string(),
  description: z.string().optional(),
});

const ZCreateTasksBatchArgs = z.object({
  area_uuid: z.string(),
  tasks: z.array(ZTaskItem),
});

const ZUpdateTaskArgs = z.object({
  task_uuid: z.string(),
}).catchall(z.unknown());

const ZSetupPlanStep = z.object({
  id: z.string(),
  label: z.string(),
  area_name: z.string(),
});

const ZCreateSetupPlanArgs = z.object({
  steps: z.array(ZSetupPlanStep),
});

const ZCompleteTaskByDescriptionArgs = z.object({
  description: z.string(),
  matched_task_uuid: z.string().optional(),
  matched_task_name: z.string().optional(),
  matched_task_points: z.number().optional(),
  create_new: z.object({
    name: z.string(),
    area_uuid: z.string(),
    points: z.number(),
    frequency_number: z.number(),
    frequency_unit: z.string(),
  }).optional(),
});

// --- Helpers ---

function formatFrequency(num: number, unit: string): string {
  if (num === 1) {
    switch (unit) {
      case 'days': return 'daily';
      case 'weeks': return 'weekly';
      case 'months': return 'monthly';
      case 'years': return 'yearly';
    }
  }
  if (num === 2 && unit === 'weeks') return 'biweekly';
  // Strip trailing 's' for display (e.g., "months" → "month")
  const singular = unit.endsWith('s') ? unit.slice(0, -1) : unit;
  return num === 1 ? singular : `every ${num} ${num === 1 ? singular : unit}`;
}

const POINTS_COLORS: Record<number, string> = {
  1: 'bg-green-500',
  2: 'bg-blue-500',
  3: 'bg-yellow-500',
  4: 'bg-orange-500',
  5: 'bg-purple-500',
};

function pointsColor(pts: number): string {
  return POINTS_COLORS[pts] ?? 'bg-muted-foreground';
}

// --- Categorized action types ---

interface AreaItem { name: string }
interface TaskItemParsed { name: string; points: number; frequency: string; isHighPriority: boolean }
interface UpdateItem { fields: string[] }
interface PlanStepItem { label: string; areaName: string }
interface FallbackItem { preview: string }

interface CompletionItem {
  description: string;
  matchedTaskName?: string;
  matchedTaskPoints?: number;
  createNew?: { name: string; points: number; frequencyNumber: number; frequencyUnit: string };
}

interface CategorizedActions {
  areas: AreaItem[];
  tasks: TaskItemParsed[];
  updates: UpdateItem[];
  planSteps: PlanStepItem[];
  completions: CompletionItem[];
  fallbacks: FallbackItem[];
}

function categorizeActions(actions: IPendingAction[]): CategorizedActions {
  const result: CategorizedActions = { areas: [], tasks: [], updates: [], planSteps: [], completions: [], fallbacks: [] };

  for (const action of actions) {
    if (action.toolName === 'complete_task_by_description') {
      const parsed = ZCompleteTaskByDescriptionArgs.safeParse(action.args);
      if (parsed.success) {
        const item: CompletionItem = { description: parsed.data.description };
        if (parsed.data.matched_task_name) {
          item.matchedTaskName = parsed.data.matched_task_name;
          item.matchedTaskPoints = parsed.data.matched_task_points;
        }
        if (parsed.data.create_new) {
          item.createNew = {
            name: parsed.data.create_new.name,
            points: parsed.data.create_new.points,
            frequencyNumber: parsed.data.create_new.frequency_number,
            frequencyUnit: parsed.data.create_new.frequency_unit,
          };
        }
        result.completions.push(item);
        continue;
      }
    }

    if (action.toolName === 'create_setup_plan') {
      const parsed = ZCreateSetupPlanArgs.safeParse(action.args);
      if (parsed.success) {
        for (const step of parsed.data.steps) {
          result.planSteps.push({ label: step.label, areaName: step.area_name });
        }
        continue;
      }
    }

    if (action.toolName === 'create_area') {
      const parsed = ZCreateAreaArgs.safeParse(action.args);
      if (parsed.success) {
        result.areas.push({ name: parsed.data.name });
        continue;
      }
    }

    if (action.toolName === 'create_task') {
      const parsed = ZCreateTaskArgs.safeParse(action.args);
      if (parsed.success) {
        result.tasks.push({
          name: parsed.data.name,
          points: parsed.data.points,
          frequency: formatFrequency(parsed.data.frequency_number, parsed.data.frequency_unit),
          isHighPriority: parsed.data.is_high_priority ?? false,
        });
        continue;
      }
    }

    if (action.toolName === 'create_tasks_batch') {
      const parsed = ZCreateTasksBatchArgs.safeParse(action.args);
      if (parsed.success) {
        for (const t of parsed.data.tasks) {
          result.tasks.push({
            name: t.name,
            points: t.points,
            frequency: formatFrequency(t.frequency_number, t.frequency_unit),
            isHighPriority: t.is_high_priority ?? false,
          });
        }
        continue;
      }
    }

    if (action.toolName === 'update_task') {
      const parsed = ZUpdateTaskArgs.safeParse(action.args);
      if (parsed.success) {
        const fields = Object.keys(parsed.data).filter((k) => k !== 'task_uuid');
        result.updates.push({ fields });
        continue;
      }
    }

    // Fallback for unrecognized or unparseable actions
    result.fallbacks.push({ preview: action.preview });
  }

  return result;
}

// --- Section renderers ---

function AreasSection({ areas }: { areas: AreaItem[] }) {
  if (areas.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
        <MapPin size={14} />
        {areas.length === 1 ? 'New Area' : `${areas.length} Areas`}
      </div>
      <div className="space-y-1">
        {areas.map((a, i) => (
          <div key={i} className="px-2.5 py-1.5 rounded-md bg-surface text-sm font-medium text-foreground">
            {a.name}
          </div>
        ))}
      </div>
    </div>
  );
}

function TasksSection({ tasks }: { tasks: TaskItemParsed[] }) {
  if (tasks.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
        <ListChecks size={14} />
        {tasks.length === 1 ? '1 Task' : `${tasks.length} Tasks`}
      </div>
      <div className="space-y-0.5">
        {tasks.map((t, i) => (
          <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md">
            <span className={`flex-shrink-0 w-5 h-5 rounded-full ${pointsColor(t.points)} text-white text-[10px] font-bold flex items-center justify-center`}>
              {t.points}
            </span>
            <span className="text-sm font-medium text-foreground flex-1 min-w-0 truncate">
              {t.name}
            </span>
            {t.isHighPriority && <Zap size={12} className="flex-shrink-0 text-amber-500" />}
            <span className="text-xs text-muted-foreground flex-shrink-0">
              {t.frequency}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function UpdatesSection({ updates }: { updates: UpdateItem[] }) {
  if (updates.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
        <Pencil size={14} />
        {updates.length === 1 ? '1 Update' : `${updates.length} Updates`}
      </div>
      <div className="space-y-0.5">
        {updates.map((u, i) => (
          <div key={i} className="px-2.5 py-1.5 text-sm text-foreground">
            Changing: {u.fields.join(', ')}
          </div>
        ))}
      </div>
    </div>
  );
}

function PlanSection({ steps }: { steps: PlanStepItem[] }) {
  if (steps.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
        <ClipboardList size={14} />
        Setup Plan — {steps.length} Rooms
      </div>
      <div className="space-y-0.5">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-[10px] font-bold flex items-center justify-center">
              {i + 1}
            </span>
            <span className="text-sm font-medium text-foreground">
              {s.areaName}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompletionsSection({ completions }: { completions: CompletionItem[] }) {
  if (completions.length === 0) return null;
  return (
    <div className="space-y-1">
      {completions.map((c, i) => {
        if (c.matchedTaskName) {
          return (
            <div key={i}>
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
                <CheckCircle size={14} />
                Complete this task?
              </div>
              <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md">
                <span className={`flex-shrink-0 w-5 h-5 rounded-full ${pointsColor(c.matchedTaskPoints ?? 1)} text-white text-[10px] font-bold flex items-center justify-center`}>
                  {c.matchedTaskPoints}
                </span>
                <span className="text-sm font-medium text-foreground flex-1 min-w-0 truncate">
                  {c.matchedTaskName}
                </span>
              </div>
            </div>
          );
        }
        if (c.createNew) {
          return (
            <div key={i}>
              <div className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground mb-1.5">
                <PlusCircle size={14} />
                Create & complete this task?
              </div>
              <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-md">
                <span className={`flex-shrink-0 w-5 h-5 rounded-full ${pointsColor(c.createNew.points)} text-white text-[10px] font-bold flex items-center justify-center`}>
                  {c.createNew.points}
                </span>
                <span className="text-sm font-medium text-foreground flex-1 min-w-0 truncate">
                  {c.createNew.name}
                </span>
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  {formatFrequency(c.createNew.frequencyNumber, c.createNew.frequencyUnit)}
                </span>
              </div>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

function FallbacksSection({ fallbacks }: { fallbacks: FallbackItem[] }) {
  if (fallbacks.length === 0) return null;
  return (
    <div className="space-y-1">
      {fallbacks.map((f, i) => (
        <p key={i} className="text-sm text-foreground">{f.preview}</p>
      ))}
    </div>
  );
}

function ActionSections({ categorized }: { categorized: CategorizedActions }) {
  return (
    <div className="space-y-3">
      <PlanSection steps={categorized.planSteps} />
      <AreasSection areas={categorized.areas} />
      <TasksSection tasks={categorized.tasks} />
      <CompletionsSection completions={categorized.completions} />
      <UpdatesSection updates={categorized.updates} />
      <FallbacksSection fallbacks={categorized.fallbacks} />
    </div>
  );
}

// --- Main component ---

interface ConfirmationCardProps {
  actions: IPendingAction[];
  onConfirm: () => void;
  onReject: () => void;
  onSuggestChanges?: () => void;
  disabled?: boolean;
}

export function ConfirmationCard({ actions, onConfirm, onReject, onSuggestChanges, disabled }: ConfirmationCardProps) {
  const allPending = actions.every((a) => a.status === 'pending');
  const allConfirmed = actions.every((a) => a.status === 'confirmed');
  const allRejected = actions.every((a) => a.status === 'rejected');

  const categorized = categorizeActions(actions);

  if (allConfirmed) {
    return (
      <div data-testid="confirmation-card" className="rounded-2xl border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 p-4">
        <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-300 font-medium mb-2">
          <Check size={16} />
          Confirmed
        </div>
        <ActionSections categorized={categorized} />
      </div>
    );
  }

  if (allRejected) {
    return (
      <div data-testid="confirmation-card" className="rounded-2xl border border-border bg-surface p-4 opacity-60">
        <div className="flex items-center gap-2 text-sm text-muted-foreground font-medium mb-2">
          <X size={16} />
          Cancelled
        </div>
        <ActionSections categorized={categorized} />
      </div>
    );
  }

  // Pending state
  return (
    <div data-testid="confirmation-card" className="rounded-2xl border border-primary/25 bg-accent p-4 shadow-soft">
      <p className="text-xs font-bold uppercase tracking-wider text-accent-foreground mb-2">
        Confirm these changes?
      </p>
      <div className="mb-3">
        <ActionSections categorized={categorized} />
      </div>
      {allPending && (
        onSuggestChanges ? (
          <div className="flex flex-col items-center gap-2">
            <div className="flex gap-2 w-full">
              <button
                onClick={onConfirm}
                disabled={disabled}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 min-h-[44px] rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 transition-colors"
              >
                <Check size={16} />
                Looks good
              </button>
              <button
                onClick={onSuggestChanges}
                disabled={disabled}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 min-h-[44px] rounded-full border border-primary/30 bg-card text-accent-foreground text-sm font-semibold hover:bg-card/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 transition-colors"
              >
                <PenLine size={16} />
                Suggest changes
              </button>
            </div>
            <button
              onClick={onReject}
              disabled={disabled}
              className="min-h-[36px] px-3 text-xs font-semibold text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={onConfirm}
              disabled={disabled}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 min-h-[44px] rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 transition-colors"
            >
              <Check size={16} />
              Looks good
            </button>
            <button
              onClick={onReject}
              disabled={disabled}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 min-h-[44px] rounded-full border border-border bg-card text-foreground text-sm font-semibold hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 transition-colors"
            >
              <X size={16} />
              Cancel
            </button>
          </div>
        )
      )}
    </div>
  );
}
